import type { StoreDetails } from '@/lib/api/stores';
import { parseTRPCError } from '@/lib/utils/trpc-error-utils';

export type BulkCurrencyStoreRef = {
  id: string;
  name: string;
};

export type BulkCurrencyUpdateResult = {
  updated: number;
  skipped: number;
  failed: Array<{ storeId: string; storeName: string; reason: string }>;
};

export const mergePresentmentCurrencies = (current: string[], toAdd: string[]) =>
  [...new Set([...current, ...toAdd])];

export const getNewPresentmentCurrencies = (current: string[], toAdd: string[]) =>
  toAdd.filter((currency) => !current.includes(currency));

export const buildBulkStoreUpdateData = (
  store: StoreDetails,
  presentmentCurrencies: string[],
) => ({
  name: store.name,
  presentmentCurrencies,
  chargeCurrencies: store.chargeCurrencies ?? [],
  baseCurrency: store.baseCurrency,
  type: store.type || 'shopify',
  credentials: store.credentials ?? null,
  multipleSubscriptionsPerCustomerEnabled: store.multipleSubscriptionsPerCustomerEnabled ?? true,
  customThankYouUrl: store.customThankYouUrl ?? null,
  config: store.config ?? null,
  applyExternalBXGYDiscountOnSubscription: store.applyExternalBXGYDiscountOnSubscription ?? false,
  digitalProduct: store.digitalProduct ?? false,
});

const isPendingCurrencyError = (message: string) =>
  message.includes('currency update is already in progress');

const isInngestError = (message: string) =>
  message.includes('Inngest API Error') || message.includes('Event key not found');

export const mapCurrencyUpdateError = (error: unknown): string => {
  const message = parseTRPCError(error).message;
  if (isPendingCurrencyError(message)) return 'currency_update_in_progress';
  if (isInngestError(message)) return 'inngest_unavailable';
  return message;
};

export const createEmptyBulkCurrencyResult = (): BulkCurrencyUpdateResult => ({
  updated: 0,
  skipped: 0,
  failed: [],
});

type BulkCurrencyUpdateDeps = {
  fetchStore: (storeId: string) => Promise<StoreDetails | null>;
  updateStore: (input: {
    storeId: string;
    updatedData: ReturnType<typeof buildBulkStoreUpdateData>;
  }) => Promise<unknown>;
};

const processStoreCurrencyUpdate = async (
  storeRef: BulkCurrencyStoreRef,
  currenciesToAdd: string[],
  deps: BulkCurrencyUpdateDeps,
) => {
  const store = await deps.fetchStore(storeRef.id);
  if (!store) throw new Error('Store not found');
  const current = store.presentmentCurrencies ?? [];
  if (getNewPresentmentCurrencies(current, currenciesToAdd).length === 0) {
    return 'skipped' as const;
  }
  const merged = mergePresentmentCurrencies(current, currenciesToAdd);
  await deps.updateStore({
    storeId: storeRef.id,
    updatedData: buildBulkStoreUpdateData(store, merged),
  });
  return 'updated' as const;
};

const runStoreCurrencyUpdate = async (
  storeRef: BulkCurrencyStoreRef,
  currenciesToAdd: string[],
  deps: BulkCurrencyUpdateDeps,
) => {
  try {
    const status = await processStoreCurrencyUpdate(storeRef, currenciesToAdd, deps);
    return { storeRef, status, reason: undefined };
  } catch (error) {
    return { storeRef, status: 'failed' as const, reason: mapCurrencyUpdateError(error) };
  }
};

const aggregateBulkOutcomes = (
  outcomes: Array<{
    storeRef: BulkCurrencyStoreRef;
    status: 'updated' | 'skipped' | 'failed';
    reason?: string;
  }>,
): BulkCurrencyUpdateResult => {
  const result = createEmptyBulkCurrencyResult();
  outcomes.forEach((outcome) => {
    if (outcome.status === 'updated') result.updated += 1;
    if (outcome.status === 'skipped') result.skipped += 1;
    if (outcome.status === 'failed') {
      result.failed.push({
        storeId: outcome.storeRef.id,
        storeName: outcome.storeRef.name,
        reason: outcome.reason ?? 'unknown_error',
      });
    }
  });
  return result;
};

export const processBulkCurrencyUpdate = async (
  stores: BulkCurrencyStoreRef[],
  currenciesToAdd: string[],
  deps: BulkCurrencyUpdateDeps,
): Promise<BulkCurrencyUpdateResult> => {
  const outcomes = await Promise.all(
    stores.map((storeRef) => runStoreCurrencyUpdate(storeRef, currenciesToAdd, deps)),
  );
  return aggregateBulkOutcomes(outcomes);
};
